// SPDX-License-Identifier: GPL-3.0-or-later
// The Twitch panel: one frame in a box inside the playfield, BESIDE the table. Twitch refuses to
// autoplay a frame that something covers at load, or that is not shown at load. So the box gets its
// place and size BEFORE it joins the page, and nothing may be painted above it. The style sheet
// (stream-bg.css) moves the plunger power readout out of the way while the panel is on.
// The box takes no pointer event and no focus: a click must not move the keys into the frame.
// The game page uses this module. The /overlay page must never import it.

import type { StreamDocument, StreamNode } from "./stream-bg.ts";
import type { PanelBox } from "./stream-config.ts";

/** The playfield as the panel uses it. A real element fits it. */
export interface StreamPlayfield {
  setAttribute(name: string, value: string): void;
  removeAttribute(name: string): void;
  append(...nodes: StreamNode[]): void;
  getBoundingClientRect(): { readonly width: number; readonly height: number };
}

/** The panel that the page keeps. */
export interface Panel {
  /** Put the box in place. Call it again after each resize. */
  place(box: PanelBox): void;
  /** Remove the box, the frame and the mark on the playfield. */
  remove(): void;
}

/** Make the panel, put it in place, and only then add it to the playfield. */
export function createPanel(
  doc: StreamDocument,
  host: StreamPlayfield,
  frame: StreamNode,
  at: PanelBox,
): Panel {
  const box = doc.createElement("div");
  box.className = "stream-panel";
  box.setAttribute("data-slot", "stream-panel");
  box.setAttribute("aria-hidden", "true");
  box.append(frame);
  let removed = false;
  const place = (next: PanelBox): void => {
    if (removed) return;
    for (const [name, value] of [
      ["left", next.left],
      ["top", next.top],
      ["width", next.width],
      ["height", next.height],
    ] as const)
      box.style.setProperty(name, `${value}px`);
  };
  place(at);
  host.setAttribute("data-stream-panel", "on");
  host.append(box);
  return {
    place,
    remove() {
      removed = true;
      box.remove();
      host.removeAttribute("data-stream-panel");
    },
  };
}
